import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import { Routes, makeSimpleRoutePath } from '../../utils/routerHelper';
import { competitionTimeZone } from '../../utils/adminDateTime';
import ScheduledStatusPanel from './ScheduledStatusPanel';
import { ScheduledCommunicationsStatus } from './scheduledTypes';

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  canViewAdminTools: boolean;
  competitionTimezone?: string;
}

interface LocalState {
  status: ScheduledCommunicationsStatus | null;
  loading: boolean;
  error: string;
}

/**
 * Dedicated scheduled-communications view.
 * Competition admins only (same as scheduled-status API).
 */
class CommunicationsScheduledPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    status: null,
    loading: true,
    error: ''
  };

  public componentDidMount() {
    this.load();
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private load = async () => {
    const { apiBase, competitionName, canViewAdminTools } = this.props;
    if (!canViewAdminTools) {
      this.setState({ loading: false, error: 'Admin access required.' });
      return;
    }
    this.setState({ loading: true, error: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/communications/scheduled-status`,
        { headers: this.authHeaders() }
      );
      if (res.status === 404) {
        this.setState({
          loading: false,
          error: 'Scheduled status is not available on this API yet. It will appear once the Phase 2B endpoint is deployed.'
        });
        return;
      }
      if (!res.ok) {
        this.setState({ loading: false, error: `Failed to load scheduled status (${res.status}).` });
        return;
      }
      const status = await res.json();
      this.setState({ status, loading: false });
    } catch (_err) {
      this.setState({ loading: false, error: 'Network error loading scheduled status.' });
    }
  }

  public render() {
    const { t, canViewAdminTools, competitionTimezone } = this.props;
    const { status, loading, error } = this.state;
    return (
      <div>
        <div className="container" style={{ maxWidth: 960, paddingTop: 24, paddingBottom: 40 }}>
          <p>
            <Link to={makeSimpleRoutePath(Routes.Communications, t)}>{t('← Communications')}</Link>
          </p>
          <div className="cc__white_box" style={{ padding: 20 }}>
            <h2>{t('Scheduled communications')}</h2>
            <AdminGuideLink sectionId="communications" label={t('How automated emails work')} t={t} />
            <p className="cc__lighttext">
              {t('Signup confirmation and launch welcome timing for this Challenge. Challenge start is when the competition opens; the welcome email may send later if configured.')}
            </p>
            {!canViewAdminTools &&
              <Alert color="danger">{t('Admin access is required to view scheduled communications.')}</Alert>
            }
            {canViewAdminTools &&
              <ScheduledStatusPanel
                status={status}
                loading={loading}
                error={error}
                t={t}
                timeZone={competitionTimezone}
                detailed={true}
              />
            }
            <div style={{ marginTop: 16 }}>
              <Button color="secondary" size="sm" onClick={this.load} disabled={loading}>
                {t('Refresh')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  canViewAdminTools: selectors.canViewAdminTools(state),
  competitionTimezone: competitionTimeZone(state.competition as { timezone?: string } | null)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(CommunicationsScheduledPage));
